import { Component, effect, inject, type OnInit, output, ViewChild, ViewContainerRef } from '@angular/core';
import { MessageComponent } from './components/message/message.component';
import { UiButtonComponent } from '@big-direkt/ui/button';
import { RatingComponent } from './components/rating/rating.component';
import { InputComponent } from './components/input/input.component';
import { WindowService } from '@big-direkt/utils/environment';
import { ChatService } from '../services/chat.service';
import { MessagesService } from '../services/messages.service';
import { Message } from '../interfaces/message';
import { MessageButton } from '../interfaces/message-button';

@Component({
    selector: 'big-chat-bot',
    templateUrl: './bot.component.html',
    imports: [MessageComponent, UiButtonComponent, RatingComponent, InputComponent],
    host: {
        class: 'flex flex-col h-full bg-alt',
    },
})
export class BotComponent implements OnInit {
    private readonly chatService = inject(ChatService);
    private readonly handoverTimeout = 3000;
    private readonly messagesService = inject(MessagesService);
    private readonly windowService = inject(WindowService);

    public readonly config = this.chatService.config;
    public readonly messages = this.messagesService.messages;

    public readonly handover = output<boolean>();

    @ViewChild('messageWrapper', { static: true, read: ViewContainerRef }) public messageWrapper!: ViewContainerRef;

    private hiddenHandoverIsCalled = false;

    public constructor() {
        effect(() => {
            const message = this.messagesService.updated();

            if (!message) {
                return;
            }

            if ((message.doHandover ?? false) && !this.hiddenHandoverIsCalled) {
                this.hiddenHandoverIsCalled = true;

                setTimeout((): void => {
                    this.handover.emit(true);
                }, this.handoverTimeout);
            }

            setTimeout((): void => {
                this.scrollToBottom();
            }, 1);
        });
    }

    public ngOnInit(): void {
        if (this.messages().length) {
            return;
        }

        const message: Message = this.messagesService.createMessage(true, this.config().bot.status?.start ?? '');
        this.messagesService.add(message);
    }

    public toggleUseNote(): void {
        this.chatService.toggleUserNote();
    }

    public scrollToBottom(): void {
        try {
            this.messageWrapper.element.nativeElement.scrollTop = this.messageWrapper.element.nativeElement.scrollHeight;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    }

    public buttonClick(button: MessageButton): void {
        if (button.payload === '/handover' || button.payload === '/_uebergabe_livechat') {
            this.handover.emit(true);

            return;
        }

        const hyperlinkMatch = /\((https?:\/\/[^\s)]+)\)/.exec(button.payload);

        if (hyperlinkMatch) {
            this.windowService.nativeWindow()?.open(hyperlinkMatch[1], '_blank');

            return;
        }

        const message: Message = this.messagesService.createMessage(false, button.payload);
        void this.messagesService.sendMessage(message, false);
    }

    public errorButtonClick(failedMessage: Message): void {
        const message: Message = this.messagesService.createMessage(false, failedMessage.text);
        this.messagesService.remove(failedMessage);
        void this.messagesService.sendMessage(message);
    }
}
